import React from 'react'
import { Chat } from '@/config'
import { Icon, View } from '@/components'
import { onUpdate } from '@codeleap/hooks'
import { MessageStatusTypes } from '../types'
import { MessageContextValue, useMessageContext } from 'stream-chat-react'

export type MessageStatusPropsWithContext = MessageContextValue & {
  lastOwnMessageReadDate?: Date | null
}

const statusConstants = Chat.Constants.messageStatus

const MessageStatusWithContext = (props: MessageStatusPropsWithContext) => {
  const user = Chat.Store.useUser()
  const {
    isMyMessage,
    lastOwnMessage,
    message,
    readBy,
    threadList,
    lastOwnMessageReadDate,
  } = props

  if (!isMyMessage() || message.status === MessageStatusTypes.FAILED || message.type === 'error') return null

  const justReadByMe = readBy?.length === 1 && readBy[0].id === user?.id
  const sending = message.status === MessageStatusTypes.SENDING
  const read = !!(readBy?.length && !justReadByMe && !threadList)
  const delivered = message.status === MessageStatusTypes.RECEIVED
  const isLastOwnMessage = lastOwnMessage?.id === message.id

  const sent =
    isLastOwnMessage &&
    message.status === 'received' &&
    !delivered &&
    !read &&
    !threadList

  if (sending) return <ClockIcon />
  if (sent) return <SingleCheckIcon />
  if (delivered) {
    return (
      <DoubleCheckIcon
        lastOwnMessageReadDate={lastOwnMessageReadDate}
        message={message}
        read={read}
        isLastOwnMessage={isLastOwnMessage}
        lastOwnMessage={lastOwnMessage}
      />
    )
  }

  return null
}

const Wrapper = ({ children }) => <View style={['ml:0.3']}>{children}</View>

const ClockIcon = () => <SingleCheckIcon />

const SingleCheckIcon = () => <Wrapper><Icon name='check' style={['chatMessage', 'color:neutralSolid500']} /></Wrapper>

const DoubleCheckIcon = ({ lastOwnMessageReadDate, message, read, isLastOwnMessage, lastOwnMessage }) => {
  const isNewerThanLastOwnMessage = lastOwnMessageReadDate && new Date(lastOwnMessageReadDate) < new Date(message.created_at)

  function getColor() {
    if (read) return statusConstants.read.color
    if (!isLastOwnMessage && !isNewerThanLastOwnMessage) return statusConstants.read.color
    return statusConstants.delivered.color
  }

  onUpdate(() => {
    if (isLastOwnMessage && read) { Chat.Store.setLastOwnMessageRead(lastOwnMessage?.created_at) }
  }, [read, isLastOwnMessage, lastOwnMessage?.created_at])

  return <Wrapper><Icon name='checks' style={['chatMessage', getColor()]} /></Wrapper>
}

const areEqual = (
  prevProps: MessageStatusPropsWithContext,
  nextProps: MessageStatusPropsWithContext,
) => {
  const { message: prevMessage, readBy: prevReadBy, lastOwnMessageReadDate: prevLastOwnMessageReadDate } = prevProps
  const { message: nextMessage, readBy: nextReadBy, lastOwnMessageReadDate: nextLastOwnMessageReadDate } = nextProps

  if (prevMessage?.type !== nextMessage?.type) return false

  if (prevMessage?.status !== nextMessage?.status) return false

  const readByEqual = JSON.stringify(prevReadBy) === JSON.stringify(nextReadBy)
  if (!readByEqual) return false

  if (prevLastOwnMessageReadDate !== nextLastOwnMessageReadDate) return false

  return true
}

const MemoizedMessageStatus = React.memo(
  MessageStatusWithContext,
  areEqual,
) as typeof MessageStatusWithContext

export type MessageStatusProps = Partial<MessageStatusPropsWithContext>

export const MessageStatus = () => {
  const messageProps = useMessageContext('MessageStatus')
  const lastOwnMessageReadDate = Chat.Store.state.use(s => s.lastOwnMessageReadDate)
  return <MemoizedMessageStatus {...messageProps} lastOwnMessageReadDate={lastOwnMessageReadDate} />
}

MessageStatus.displayName = 'MessageStatus{messageSimple{status}}'
